































































































































































































































import Vue from 'vue'
import {
  AggregationJob,
  DataExplorerService,
  ExplorerSource
} from '@/api/services/data-explorer'

interface ComponentData {
  tab: 'documents' | 'aggregation'
  documents: any[]
  total: number
  pagination: { page: number; perPage: number }
  loading: boolean
  error: string
  filterText: string
  sortText: string
  editorVisible: boolean
  editorText: string
  editorTitle: string
  editorError: string
  editingId: string | null
  saving: boolean
  deleteVisible: boolean
  pendingDeleteId: string | null
  deleting: boolean
  pipelineText: string
  aggregationRunning: boolean
  aggregationError: string
  job: AggregationJob | null
  pollTimer: any
}

export default Vue.extend({
  props: {
    source: {
      type: String as () => ExplorerSource,
      required: true
    },
    collection: {
      type: String,
      required: true
    }
  },

  data(): ComponentData {
    return {
      tab: 'documents',
      documents: [],
      total: 0,
      pagination: { page: 1, perPage: 20 },
      loading: false,
      error: '',
      filterText: '',
      sortText: '',
      editorVisible: false,
      editorText: '',
      editorTitle: 'Éditer le document',
      editorError: '',
      editingId: null,
      saving: false,
      deleteVisible: false,
      pendingDeleteId: null,
      deleting: false,
      pipelineText: '[\n  { "$match": {} },\n  { "$limit": 10 }\n]',
      aggregationRunning: false,
      aggregationError: '',
      job: null,
      pollTimer: null
    }
  },

  computed: {
    totalPages(): number {
      return Math.max(1, Math.ceil(this.total / this.pagination.perPage))
    }
  },

  watch: {
    source() {
      this.reset()
    },
    collection() {
      this.reset()
    }
  },

  beforeDestroy() {
    this.stopPolling()
  },

  methods: {
    reset() {
      this.stopPolling()
      this.documents = []
      this.total = 0
      this.pagination.page = 1
      this.error = ''
      this.job = null
      this.aggregationError = ''
      this.tab = 'documents'
      this.fetchDocuments()
    },

    pretty(value: any): string {
      return JSON.stringify(value, null, 2)
    },

    documentKey(doc: any, index: number): string {
      if (doc && doc._id && doc._id.$oid) return doc._id.$oid
      if (doc && doc._id) return String(doc._id)
      if (doc && doc.id) return String(doc.id)
      return `#${index + 1}`
    },

    errorMessage(error: any): string {
      const data = error?.response?.data
      if (data) {
        if (Array.isArray(data.message)) {
          return data.message
            .map((m: any) => m.constraint || m.message || JSON.stringify(m))
            .join(', ')
        }
        if (data.message) return data.message
      }
      return error?.message || 'Une erreur est survenue'
    },

    async fetchDocuments() {
      this.loading = true
      this.error = ''

      try {
        const response = await DataExplorerService.listDocuments(
          this.source,
          this.collection,
          {
            page: this.pagination.page,
            perPage: this.pagination.perPage,
            filter: this.filterText.trim() || undefined,
            sort: this.sortText.trim() || undefined
          }
        )

        this.documents = response.result || []
        this.total = response.count || 0
      } catch (error) {
        this.error = this.errorMessage(error)
        this.documents = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },

    reload() {
      this.pagination.page = 1
      this.fetchDocuments()
    },

    prev() {
      if (this.pagination.page > 1) {
        this.pagination.page--
        this.fetchDocuments()
      }
    },

    next() {
      if (this.pagination.page < this.totalPages) {
        this.pagination.page++
        this.fetchDocuments()
      }
    },

    openCreate() {
      this.editingId = null
      this.editorTitle = 'Nouveau document'
      this.editorText = '{\n  \n}'
      this.editorError = ''
      this.editorVisible = true
    },

    openEdit(doc: any) {
      const id = this.documentKey(doc, 0)
      this.editingId = id
      this.editorTitle = `Éditer ${id}`
      this.editorError = ''
      this.editorText = this.pretty(doc)
      this.editorVisible = true
    },

    parseEditorDocument(): any {
      let parsed: any
      try {
        parsed = JSON.parse(this.editorText)
      } catch (error) {
        throw new Error("Le document n'est pas un JSON valide")
      }

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Le document doit être un objet JSON')
      }

      return parsed
    },

    async saveDocument() {
      this.editorError = ''

      let document: any
      try {
        document = this.parseEditorDocument()
      } catch (error) {
        this.editorError = error.message
        return
      }

      this.saving = true

      try {
        if (this.editingId) {
          const withoutId = { ...document }
          delete withoutId._id
          await DataExplorerService.updateDocument(
            this.source,
            this.collection,
            this.editingId,
            withoutId
          )
        } else {
          await DataExplorerService.createDocument(
            this.source,
            this.collection,
            document
          )
        }

        this.editorVisible = false
        this.fetchDocuments()
      } catch (error) {
        this.editorError = this.errorMessage(error)
      } finally {
        this.saving = false
      }
    },

    askDelete(doc: any) {
      this.pendingDeleteId = this.documentKey(doc, 0)
      this.deleteVisible = true
    },

    async confirmDelete() {
      if (!this.pendingDeleteId) return

      this.deleting = true

      try {
        await DataExplorerService.deleteDocument(
          this.source,
          this.collection,
          this.pendingDeleteId
        )
        this.deleteVisible = false
        this.pendingDeleteId = null
        this.fetchDocuments()
      } catch (error) {
        this.error = this.errorMessage(error)
        this.deleteVisible = false
      } finally {
        this.deleting = false
      }
    },

    parsePipeline(): any[] {
      let parsed: any
      try {
        parsed = JSON.parse(this.pipelineText)
      } catch (error) {
        throw new Error("La pipeline n'est pas un JSON valide")
      }

      if (!Array.isArray(parsed) || parsed.length === 0) {
        throw new Error('La pipeline doit être un tableau JSON non vide')
      }

      return parsed
    },

    async runAggregation() {
      this.aggregationError = ''
      this.job = null
      this.stopPolling()

      let pipeline: any[]
      try {
        pipeline = this.parsePipeline()
      } catch (error) {
        this.aggregationError = error.message
        return
      }

      this.aggregationRunning = true

      try {
        const { jobId } = await DataExplorerService.runAggregation(
          this.source,
          this.collection,
          pipeline
        )
        await this.pollJob(jobId)
      } catch (error) {
        this.aggregationError = this.errorMessage(error)
        this.aggregationRunning = false
      }
    },

    async pollJob(jobId: string) {
      try {
        const job = await DataExplorerService.getAggregation(this.source, jobId)
        this.job = job

        if (job.status === 'completed' || job.status === 'failed') {
          this.aggregationRunning = false
          return
        }

        this.pollTimer = setTimeout(() => this.pollJob(jobId), 1200)
      } catch (error) {
        this.aggregationError = this.errorMessage(error)
        this.aggregationRunning = false
      }
    },

    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    }
  }
})
